import { Table, Tag, Space, Button, Popconfirm, Modal, InputNumber, Input, Select, Switch, message } from "antd";
import { DeleteOutlined, EyeOutlined, SplitCellsOutlined, MergeCellsOutlined, SearchOutlined } from "@ant-design/icons";
import { useState, useMemo } from "react";
import Header from "../_shared/components/Header";
import { useGetJobsQuery, useDeleteJobMutation, useSplitJobMutation, useUnsplitJobMutation } from "../../../redux/api/scheduling";
import { formatDate } from "../_shared/utils/dateUtils";
import { useShowThumbnails } from "../_shared/utils/useShowThumbnails";
import JobDetail from "./JobDetail";

const TIMELINESS_COLORS: Record<string, string> = {
  on_track: "green",
  at_risk: "orange",
  late: "red",
};

const LEAD_TIME_COLORS: Record<string, string> = {
  EX1: "red",
  EX2: "volcano",
  EX3: "orange",
  EX4: "gold",
  STD: "blue",
};

export default function Jobs() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    params.status = statusFilter || "all";
    return params;
  }, [search, statusFilter]);
  const { data, isLoading, refetch } = useGetJobsQuery(queryParams);
  const [deleteJob] = useDeleteJobMutation();
  const [splitJob] = useSplitJobMutation();
  const [unsplitJob] = useUnsplitJobMutation();
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [splitModalJobId, setSplitModalJobId] = useState<number | null>(null);
  const [splitCount, setSplitCount] = useState(2);
  const [showThumbnails, setShowThumbnails] = useShowThumbnails();

  const jobs = data?.results || data || [];

  const handleDelete = async (id: number) => {
    await deleteJob(id).unwrap();
    message.success("Job deleted");
    refetch();
  };

  const handleSplit = async () => {
    if (!splitModalJobId) return;
    try {
      await splitJob({ id: splitModalJobId, split_count: splitCount }).unwrap();
      message.success(`Job split into ${splitCount} sub-jobs`);
      setSplitModalJobId(null);
      setSplitCount(2);
      refetch();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error splitting job");
    }
  };


  const handleUnsplit = async (id: number) => {
    try {
      await unsplitJob(id).unwrap();
      message.success("Sub-jobs merged back into a single job");
      refetch();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error unsplitting job");
    }
  };

  const thumbnailColumn = {
    title: "",
    key: "thumbnail",
    width: 64,
    render: (_: any, record: any) =>
      record.thumbnail_file ? (
        <img className="h-12" src={record.thumbnail_file} alt="part" />
      ) : null,
  };

  const baseColumns = [
    {
      title: "Order",
      dataIndex: "order_name",
      key: "order_name",
      width: 160,
      sorter: (a: any, b: any) => (a.order_name || "").localeCompare(b.order_name || ""),
      render: (v: string, record: any) => (
        <>
          {v}
          {record.split_group && (
            <Tag color="purple" style={{ marginLeft: 4 }}>
              S{(record.split_index ?? 0) + 1}
            </Tag>
          )}
        </>
      ),
    },
    { title: "Part", dataIndex: "part_name", key: "part_name" },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 70,
      sorter: (a: any, b: any) => (a.quantity || 0) - (b.quantity || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => <Tag color={v === "active" ? "blue" : v === "complete" ? "green" : v === "cancelled" ? "red" : "default"}>{v}</Tag>,
    },
    {
      title: "Lead Time",
      dataIndex: "lead_time",
      key: "lead_time",
      filters: [
        { text: "EX1", value: "EX1" },
        { text: "EX2", value: "EX2" },
        { text: "EX3", value: "EX3" },
        { text: "EX4", value: "EX4" },
        { text: "STD", value: "STD" },
      ],
      onFilter: (value: any, record: any) => record.lead_time === value,
      render: (v: string) => <Tag color={LEAD_TIME_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Timeliness",
      dataIndex: "timeliness",
      key: "timeliness",
      filters: [
        { text: "On Track", value: "on_track" },
        { text: "At Risk", value: "at_risk" },
        { text: "Late", value: "late" },
      ],
      onFilter: (value: any, record: any) => record.timeliness === value,
      render: (v: string) => <Tag color={TIMELINESS_COLORS[v] || "default"}>{v?.replace(/_/g, " ").toUpperCase()}</Tag>,
    },
    {
      title: "Job Mix",
      dataIndex: "job_mix",
      key: "job_mix",
      filters: [
        { text: "SS", value: "SS" },
        { text: "FS", value: "FS" },
        { text: "LO", value: "LO" },
        { text: "WE", value: "WE" },
      ],
      onFilter: (value: any, record: any) => record.job_mix === value,
      render: (v: string) => v ? <Tag>{v}</Tag> : "-",
    },
    {
      title: "Difficulty",
      dataIndex: "run_difficulty",
      key: "run_difficulty",
      width: 80,
      sorter: (a: any, b: any) => (a.run_difficulty ?? 5) - (b.run_difficulty ?? 5),
      render: (v: number) => v ?? 5,
    },
    {
      title: "Machine Group",
      dataIndex: "machine_group_summary",
      key: "machine_group_summary",
      render: (v: string) => v || "-",
    },
    {
      title: "Method",
      dataIndex: "production_method",
      key: "production_method",
      width: 100,
      filters: [
        { text: "Fastlane", value: "Fastlane" },
        { text: "Swiss", value: "Swiss" },
      ],
      onFilter: (value: any, record: any) => (record.production_method || "").toLowerCase() === String(value).toLowerCase(),
      render: (v: string) => v ? <Tag color={v.toLowerCase() === "fastlane" ? "blue" : "purple"}>{v}</Tag> : "-",
    },
    {
      title: "Due Date",
      dataIndex: "required_completion_date",
      key: "required_completion_date",
      sorter: (a: any, b: any) => new Date(a.required_completion_date || 0).getTime() - new Date(b.required_completion_date || 0).getTime(),
      render: (v: string) => v ? formatDate(v) : "-",
    },
    {
      title: "Adj. Due Date",
      dataIndex: "adjusted_required_completion_date",
      key: "adjusted_required_completion_date",
      sorter: (a: any, b: any) => new Date(a.adjusted_required_completion_date || 0).getTime() - new Date(b.adjusted_required_completion_date || 0).getTime(),
      render: (v: string) => v ? formatDate(v) : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" onClick={() => setSelectedJobId(record.id)}>View</Button>
          {record.status === "active" && !record.split_group && (
            <Button
              icon={<SplitCellsOutlined />}
              size="small"
              onClick={() => { setSplitModalJobId(record.id); setSplitCount(2); }}
            >
              Split
            </Button>
          )}
          {record.split_group && (() => {
            const siblingCount = (jobs as any[]).filter((j: any) => j.split_group === record.split_group).length;
            return (
              <Popconfirm
                title="Unsplit this job?"
                description={`All ${siblingCount} sub-jobs in this split group will be merged back into the original job. Scheduling data will be cleared.`}
                onConfirm={() => handleUnsplit(record.id)}
              >
                <Button icon={<MergeCellsOutlined />} size="small">Unsplit</Button>
              </Popconfirm>
            );
          })()}
          <Popconfirm title="Delete this job?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const columns = showThumbnails ? [thumbnailColumn, ...baseColumns] : baseColumns;

  if (selectedJobId) {
    return (
      <div className="w-full p-4">
        <Button onClick={() => setSelectedJobId(null)} className="mb-4">← Back to Jobs</Button>
        <JobDetail jobId={selectedJobId} />
      </div>
    );
  }

  return (
    <div className="w-full p-4">
      <Header title="Jobs" />
      <div style={{ marginBottom: 12, display: "flex", gap: 12, alignItems: "center" }}>
        <Input.Search
          placeholder="Search by order or part name..."
          allowClear
          onSearch={(value) => setSearch(value)}
          onChange={(e) => { if (!e.target.value) setSearch(""); }}
          style={{ width: 350 }}
          prefix={<SearchOutlined />}
        />
        <Select
          value={statusFilter}
          onChange={(val) => setStatusFilter(val)}
          style={{ width: 150 }}
          options={[
            { label: "All", value: "" },
            { label: "Active", value: "active" },
            { label: "Complete", value: "complete" },
            { label: "On Hold", value: "on_hold" },
            { label: "Cancelled", value: "cancelled" },
          ]}
        />
        <Space size="small">
          <Switch checked={showThumbnails} onChange={setShowThumbnails} size="small" />
          <span>Show thumbnails</span>
        </Space>
      </div>
      <Table dataSource={jobs} columns={columns} loading={isLoading} rowKey="id" size="small" />

      <Modal
        title="Split Job"
        open={splitModalJobId !== null}
        onCancel={() => setSplitModalJobId(null)}
        onOk={handleSplit}
        okText="Split"
      >
        <p className="mb-2">
          Only <strong>multi-machine tasks</strong> will be divided across the sub-jobs.
          All other tasks stay on the first sub-job at full duration.
        </p>
        <div className="flex items-center gap-2">
          <span>Split into</span>
          <InputNumber min={2} max={20} value={splitCount} onChange={(v) => setSplitCount(v || 2)} />
          <span>sub-jobs</span>
        </div>
      </Modal>
    </div>
  );
}
